import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestSummaryReportInterFace,
  SummaryReportInterFace,
} from '../../../interface/summary-reports-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableSummaryReportFiltersApplied,
  fetchSummaryReport,
  fetchListSummaryReportFilters,
  updateSummaryReportFilters,
  setSortData,
} from '../../../redux/slices/SummaryReportSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../utils/api/station-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}

/**
 * ReportsSummary
 * @returns {any} The rendered component
 */
function ReportsSummary(properties: ReportLeftTopContent) {
  const { t } = useTranslation('sidePanel');
  const { leftTopContent } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    SummaryReport,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.SummaryReports);
  const column = [
    {
      label: t('devicesName'),
      accessorKey: 'chargingPointName' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('deviceSnShort'),
      accessorKey: 'chargingPointSn' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'chargingPointLocation' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('totalUptime'),
      accessorKey: 'totalUptimeMin' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return (
          <p>
            {ToFixedValue(currentRow?.totalRevenue)}
          </p>
        );
      },
    },
    {
      label: t('totalEnergyDispensed'),
      accessorKey: 'totalPowerConsumed' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        const value = Number(currentRow?.totalPowerConsumed) / 1000;
        return (
          <p>
            {ToFixedValue(value)} {t('kwh')}
          </p>
        );
      },
    },
    {
      label: t('avgChargingSessionsPerDay'),
      accessorKey: 'averageTransactionDurationInMinutes' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return <p>{ToFixedValue(currentRow?.averageTransactionDurationInMinutes)}</p>;
      },
    },
    {
      label: t('avgSessionDuration'),
      accessorKey: 'totalSessions' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {RequestSummaryReportInterFace} data The request data
   */
  const fetchData = (data: RequestSummaryReportInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchSummaryReport({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('devicesName'), accessorKey: 'chargingPointName' },
    { label: t('deviceSnShort'), accessorKey: 'chargingPointSn' },
    { label: t('stationLocation'), accessorKey: 'chargingPointLocation' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('totalEnergyDispensed'), accessorKey: 'totalPowerConsumed' },
    { label: t('avgChargingSessionsPerDay'), accessorKey: 'totalSessions' },
    { label: t('avgSessionDuration'), accessorKey: 'averageTransactionDurationInMinutes' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    setExportLoader(true);
    stationManagementApi
      .fetchReportSummaryExport({
        ...queryParameters,
        submissionStatus: 'approved',
        sortBy,
        sortField,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: SummaryReportInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || t('unavailable');
              default:
                return (row as any)[col?.accessorKey] || t('unavailable');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {'asc' | 'desc'} sortData.sortBy The sort order
   * @param {string} sortData.sortField The sort field
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSummaryReportFiltersApplied());
    }
  }, [filters]);
  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('SummaryReportFilters'); // string | null

    if (SummaryReport.length === 0 || lastId !== currentId) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSummaryReportFiltersApplied());
    }
    sessionStorage.setItem('SummaryReportFilters', currentId);
  }, [selectedChargingPoint?.id]);
  return (
    <div>
      {' '}
      <CustomTable
        version="v2"
        header={column}
        data={SummaryReport}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateSummaryReportFilters}
        filterListToDispatch={fetchListSummaryReportFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        ReferenceCurrentPages={handleRefresh}
        leftTopContent={leftTopContent}
        pageLimitChange={pageNateLimitChange}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        disableHeader={false}
        rightTopContent={<> </>}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default ReportsSummary;
